import React, { useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';

const presetLocations = [
  { name: 'New York', lat: 40.7128, lng: -74.0060 },
  { name: 'London', lat: 51.5074, lng: -0.1278 },
  { name: 'Tokyo', lat: 35.6762, lng: 139.6503 },
  { name: 'Sydney', lat: -33.8688, lng: 151.2093 },
  { name: 'Rio de Janeiro', lat: -22.9068, lng: -43.1729 }
];

const LocationModal = ({
  show,
  onClose,
  locationInput,
  setLocationInput,
  searchResults,
  isLoading,
  handleLocationChange,
  selectLocation,
  getCurrentLocation
}) => {
  const dialogRef = useRef(null);

  // Trap focus and handle Escape key
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Change location"
      ref={dialogRef}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold mb-4">Change Location</h2>

        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search for a city..."
            value={locationInput}
            onChange={handleLocationChange}
            className="w-full p-3 border border-gray-300 rounded-lg min-h-[44px]"
            autoFocus
            aria-label="Search for a city"
          />
          {isLoading && (
            <div className="absolute right-3 top-3">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" aria-label="Loading"></div>
            </div>
          )}
        </div>

        <button
          className="w-full mb-4 flex items-center justify-center px-4 py-3 bg-blue-500 text-white rounded-lg min-h-[44px] hover:bg-blue-600 transition-colors"
          onClick={getCurrentLocation}
        >
          <MapPin size={18} className="mr-2" />
          Use my current location
        </button>

        {searchResults.length > 0 && (
          <div className="mb-4">
            <h3 className="font-medium mb-2">Search Results:</h3>
            <div className="max-h-40 overflow-y-auto">
              {searchResults.map((location, index) => (
                <button
                  key={index}
                  className="w-full text-left p-3 hover:bg-gray-100 rounded min-h-[44px]"
                  onClick={() => selectLocation(location)}
                >
                  {location.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="font-medium mb-2">Popular Cities:</h3>
          <div className="grid grid-cols-2 gap-2">
            {presetLocations.map((location, index) => (
              <button
                key={index}
                className="p-3 bg-gray-100 hover:bg-gray-200 rounded text-center min-h-[44px] transition-colors"
                onClick={() => selectLocation(location)}
              >
                {location.name}
              </button>
            ))}
          </div>
        </div>

        <button
          className="w-full mt-4 px-4 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg min-h-[44px] transition-colors"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default LocationModal;
export { presetLocations };
